import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import Typography from '@mui/material/Typography'
import InfoIconOutlined from '@mui/icons-material/InfoOutlined'

import { getStyles } from './useServiceSummaryStyles'
import {
  currencyString,
  ENTITY_TYPES,
  getFormattedAddress,
  LocationDTO,
} from '@sales-center/common'
import { ServiceDialog } from '@user/components/Services'
import { IAddOpportunityFormModel } from '../../../typings'
import { Services } from '@user/components/Services/Services'
import { selectAllLeadOppProducts } from '@user/store/leadOppProducts'
import { LightTooltip, toast } from '@sales-center/components'
import { useTheme } from '@mui/material/styles'

type Totals = {
  initial: number
  first: number
  subsequent: number
  total: number
}

type Props = {
  billTo: LocationDTO
  branchId: string
  entityId: number
  serviceLocations: LocationDTO[]
  serviceLocationsLoading: boolean
  entityType: ENTITY_TYPES
  createNewOpp?: boolean
}

export function ServiceSummary(props: Props) {
  const {
    billTo: { street1, street2, city, stateAbbreviation, postalCode },
    branchId,
    entityId,
    serviceLocations,
    serviceLocationsLoading,
    entityType,
  } = props
  const [currentSubmitCount, setCurrentSubmitCount] = useState(0)

  const [servicesDialogOpen, setServicesDialogOpen] = useState<boolean>(false)

  const serviceProducts = useSelector(selectAllLeadOppProducts)

  const { errors, setFieldValue, touched, values, submitCount } =
    useFormikContext<IAddOpportunityFormModel>()

  const useSystemName = useMemo(
    () => !!values.useSystemName,
    [values.useSystemName],
  )

  const referralDiscountId = useMemo(
    () => values.referral?.referralDiscountId,
    [values.referral],
  )

  const onCloseServicesDialog = useCallback(() => {
    setServicesDialogOpen(false)
  }, [setServicesDialogOpen])

  const onViewServicesAddedClick = useCallback(() => {
    setServicesDialogOpen(true)
  }, [setServicesDialogOpen])

  const formattedAddress = useMemo(() => {
    return getFormattedAddress(
      street1 || '',
      street2 || '',
      city || '',
      stateAbbreviation || '',
      postalCode || '',
    )
  }, [city, postalCode, stateAbbreviation, street1, street2])

  useEffect(() => {
    const productsCsv = serviceProducts.map((p) => p.name).join(', ')
    setFieldValue('products', productsCsv)
  }, [serviceProducts, setFieldValue])

  useEffect(() => {
    if (
      submitCount > 0 &&
      submitCount != currentSubmitCount &&
      errors.products &&
      touched.products
    ) {
      toast.error('Service or Product Required')
      setCurrentSubmitCount(submitCount)
    }
  }, [errors, touched, submitCount, setCurrentSubmitCount, currentSubmitCount])

  const products = useSelector(selectAllLeadOppProducts)

  const { initial, subsequent, total } = useMemo(
    () =>
      products.reduce<Totals>(
        (previous, current): Totals => {
          return {
            initial: previous.initial + current.initialPrice,
            first: previous.first + current.totalFirst,
            subsequent: previous.subsequent + current.totalSubsequent,
            total:
              previous.total +
              (current.initialPrice +
                current.recurringPrice *
                  (current.subtractOne
                    ? current.annualOccurrences - 1
                    : current.annualOccurrences)) *
                current.quantity,
          }
        },
        {
          initial: 0,
          first: 0,
          subsequent: 0,
          total: 0,
        },
      ),
    [products],
  )

  const styles = getStyles(useTheme())

  return (
    <Paper
      sx={{
        px: 3,
        pb: 1,
        pt: 2,
      }}
      variant="outlined"
    >
      <Stack direction="row" mb={2} gap={0.5} alignItems="center">
        <Typography variant="h6">Service Summary</Typography>
        <LightTooltip
          arrow
          tooltipWidth="280px"
          tooltipShadow={2}
          title={
            <Stack
              sx={{
                px: '8px',
                py: '8px',
              }}
              spacing={1}
            >
              <Box>
                <Typography variant="subtitle2">Initial Value</Typography>
                <Typography variant="body2">
                  The initial amount paid to initiate service (does not include
                  sales tax)
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Annual Value</Typography>
                <Typography variant="body2">
                  The total recurring amount paid over a 1-year period (does not
                  include sales tax)
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Total Value</Typography>
                <Typography variant="body2">
                  The total annual amount including the initial value (does not
                  include sales tax)
                </Typography>
              </Box>
            </Stack>
          }
          placement="bottom"
        >
          <InfoIconOutlined sx={{ fontSize: 20, color: '#0E283F' }} />
        </LightTooltip>
      </Stack>
      <Stack spacing={1}>
        <Stack
          direction="row"
          gap={0.5}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="body1">Initial Value</Typography>
          <Typography variant="body1">
            {'$' + currencyString(initial)}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          gap={0.5}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="body1">Annual Value</Typography>
          <Typography variant="body1">
            {'$' + currencyString(subsequent)}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          gap={0.5}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="body1">Total Value</Typography>
          <Typography fontWeight="700" variant="h7">
            {'$' + currencyString(total)}
          </Typography>
        </Stack>
      </Stack>
      <Button
        sx={{ mt: 2, textTransform: 'none' }}
        disabled={!serviceProducts.length}
        onClick={onViewServicesAddedClick}
        variant="text"
        color="primary"
        data-test-id="viewServicesAddedButton"
      >
        View Services Added ({serviceProducts.length})
      </Button>
      {/* {errors.products && touched.products && (
        <Box className={useStyles.validationError}>{errors.products}</Box>
      )} */}
      <Services
        address={formattedAddress}
        branchId={branchId}
        buttonFullWidth={true}
        buttonVariant="contained"
        buttonClassName={styles.addServicesButton}
        entityId={entityId}
        entityType={entityType}
        serviceLocations={serviceLocations}
        serviceLocationsLoading={serviceLocationsLoading}
        variant="basic"
        createNewOpp
        newScreen
      />
      <ServiceDialog
        {...props}
        address={formattedAddress}
        entityType={entityType}
        minimumServicesCount={1}
        deleteServiceWarningMessage="Opportunities require at least one product or service"
        open={servicesDialogOpen}
        onClose={onCloseServicesDialog}
        openAddServiceDrawerOnMount={false}
        readOnly={false}
        referralDiscountId={referralDiscountId}
        serviceLocations={serviceLocations}
        locationSource="new"
        useSystemName={useSystemName}
      />
    </Paper>
  )
}
